import { useNavigate } from "@tanstack/react-router";

import { useCurrentWorkspace } from "@/api/resources/workspaces/hooks";

export const useNavigationAnonymous = () => {
  const navigate = useNavigate();

  const goToRedirect = (redirect?: string) => {
    void navigate({ to: redirect });
  };

  const goToLogin = () => {
    void navigate({ to: "/login" });
  };

  const goToLoginSSO = () => {
    void navigate({ to: "/login-sso" });
  };

  const goToLogout = () => {
    void navigate({ to: "/logout" });
  };

  const goToRegister = () => {
    void navigate({ to: "/register" });
  };

  const goToForgotPassword = () => {
    void navigate({ to: "/account/password/request" });
  };

  const goToSetPassword = () => {
    void navigate({ to: "/account/password/set" });
  };
  const goToResetPassword = () => {
    void navigate({ to: "/account/password/reset" });
  };
  const goToAccountSettings = () => {
    void navigate({ to: "/account/profile" });
  };

  const goToEndOfTrial = (redirect?: string) => {
    void navigate({
      to: "/end-of-trial",
      search: {
        redirect,
      },
    });
  };

  const goToOnboarding = () => {
    void navigate({ to: "/welcome" });
  };

  const goToDashboard = () => {
    void navigate({ to: "/" });
  };

  return {
    goToRedirect,
    goToLogin,
    goToLoginSSO,
    goToLogout,
    goToRegister,
    goToForgotPassword,
    goToSetPassword,
    goToResetPassword,
    goToAccountSettings,
    goToEndOfTrial,
    goToOnboarding,
    goToDashboard,
  };
};

export const useNavigation = () => {
  const navigate = useNavigate();
  const { data: currentWorkspace } = useCurrentWorkspace();

  const goToWorkspace = (workspace_id: string) => {
    void navigate({
      to: "/workspaces/$workspace_id",
      params: { workspace_id: workspace_id },
    });
  };

  const goToCustomerWorkspace = (customer_workspace_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/customer-workspaces/$customer_workspace_id",
        params: { workspace_id: currentWorkspace.id, customer_workspace_id: customer_workspace_id },
      });
    }
  };

  const goToWorkspaceSettings = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/details",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToWorkspaceUsers = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/users",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToWorkspaceUser = (user_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/users/$user_id",
        params: { workspace_id: currentWorkspace.id, user_id: user_id },
      });
    }
  };

  const goToKnowledgeHubFiles = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/knowledge-hub/files",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToKnowledgeHubFile = (file_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/knowledge-hub/files/$file_id",
        params: { workspace_id: currentWorkspace.id, file_id: file_id },
      });
    }
  };

  const goToDeployments = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToDeploymentSettings = (deployment_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments/$deployment_id/settings",
        params: { workspace_id: currentWorkspace.id, deployment_id: deployment_id },
      });
    }
  };

  const goToDeploymentUsers = (deployment_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments/$deployment_id/users",
        params: { workspace_id: currentWorkspace.id, deployment_id: deployment_id },
      });
    }
  };

  const goToDeploymentUser = (deployment_id: string, user_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments/$deployment_id/users/$user_id",
        params: { workspace_id: currentWorkspace.id, deployment_id: deployment_id, user_id: user_id },
      });
    }
  };

  const goToDeploymentPublicPortal = (deployment_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments/$deployment_id/customer-portal",
        params: { workspace_id: currentWorkspace.id, deployment_id: deployment_id },
      });
    }
  };

  const goToDevices = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/devices",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToDevice = (device_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/devices/$device_id",
        params: { workspace_id: currentWorkspace.id, device_id: device_id },
      });
    }
  };

  const goToDeviceError = (device_error_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/device-data/errors/$device_error_id",
        params: { workspace_id: currentWorkspace.id, device_error_id: device_error_id },
      });
    }
  };

  const goToDeviceDataTrigger = (device_data_trigger_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/device-data/triggers/$device_data_trigger_id",
        params: { workspace_id: currentWorkspace.id, device_data_trigger_id: device_data_trigger_id },
      });
    }
  };

  const goToDevicePart = (device_id: string, part_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/devices/$device_id/parts/$part_id",
        params: { workspace_id: currentWorkspace.id, device_id: device_id, part_id: part_id },
      });
    }
  };

  const goToCases = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/cases",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCase = (case_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/cases/$case_id",
        params: { workspace_id: currentWorkspace.id, case_id: case_id },
      });
    }
  };

  const goToCaseTemplates = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/knowledge-hub/case-templates",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCaseTemplate = (case_template_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/knowledge-hub/case-templates/$case_template_id",
        params: { workspace_id: currentWorkspace.id, case_template_id: case_template_id },
      });
    }
  };

  const goToChecklistTemplates = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/knowledge-hub/checklist-templates",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToChecklistTemplate = (checklist_template_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/knowledge-hub/checklist-templates/$checklist_template_id",
        params: { workspace_id: currentWorkspace.id, checklist_template_id: checklist_template_id },
      });
    }
  };

  const goToMaintenances = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToMaintenance = (maintenance_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances/$maintenance_id",
        params: { workspace_id: currentWorkspace.id, maintenance_id: maintenance_id },
      });
    }
  };

  const goToMaintenanceDevice = (maintenance_id: string, device_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances/$maintenance_id/devices/$device_id",
        params: { workspace_id: currentWorkspace.id, maintenance_id: maintenance_id, device_id: device_id },
      });
    }
  };

  const goToServiceJobs = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/service-jobs",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToServiceJob = (service_job_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/service-jobs/$service_job_id",
        params: { workspace_id: currentWorkspace.id, service_job_id: service_job_id },
      });
    }
  };

  const goToMaintenanceStrategies = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenance-strategies",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToMaintenanceStrategy = (maintenance_strategy_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenance-strategies/$maintenance_strategy_id",
        params: { workspace_id: currentWorkspace.id, maintenance_strategy_id: maintenance_strategy_id },
      });
    }
  };

  const goToInventoryParts = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/parts",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToIncomingInventoryPart = (part_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/incoming/$part_id",
        params: { workspace_id: currentWorkspace.id, part_id: part_id },
      });
    }
  };

  const goToInventoryPart = (part_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/parts/$part_id",
        params: { workspace_id: currentWorkspace.id, part_id: part_id },
      });
    }
  };

  const goToInventoryPartsHistories = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/activities",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCustomInventoryDevices = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/custom-inventory/devices",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCustomInventoryParts = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/custom-inventory/parts",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCustomInventoryDeviceModel = (device_model_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/custom-inventory/device-models/$device_model_id",
        params: { workspace_id: currentWorkspace.id, device_model_id: device_model_id },
      });
    }
  };

  const goToDeviceTags = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/tags/device-tags",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToMaintenanceTags = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/tags/maintenance-tags",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCaseTags = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/tags/case-tags",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  return {
    goToWorkspace,
    goToCustomerWorkspace,
    goToWorkspaceSettings,
    goToWorkspaceUsers,
    goToWorkspaceUser,
    goToKnowledgeHubFiles,
    goToKnowledgeHubFile,
    goToDeployments,
    goToDeploymentSettings,
    goToDeploymentUsers,
    goToDeploymentUser,
    goToDeploymentPublicPortal,
    goToDevices,
    goToDevice,
    goToDeviceError,
    goToDeviceDataTrigger,
    goToDevicePart,
    goToCases,
    goToCase,
    goToCaseTemplates,
    goToCaseTemplate,
    goToChecklistTemplates,
    goToChecklistTemplate,
    goToMaintenances,
    goToMaintenance,
    goToMaintenanceDevice,
    goToServiceJobs,
    goToServiceJob,
    goToMaintenanceStrategies,
    goToMaintenanceStrategy,
    goToIncomingInventoryPart,
    goToInventoryParts,
    goToInventoryPart,
    goToInventoryPartsHistories,
    goToCustomInventoryDevices,
    goToCustomInventoryParts,
    goToCustomInventoryDeviceModel,
    goToDeviceTags,
    goToMaintenanceTags,
    goToCaseTags,
  };
};
